import { type FC, useCallback } from 'react';

import { DiscontinueReason } from '@monorepo-types/dc/smartthermostat';

import {
  createSmartThermostatCancellationReasonMachine,
  type SmartThermostatCancellationReasonMachineContext,
} from '../../machines/smartThermostatCancellationReasonMachine';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';
import type { CancellationReasonContentName } from '../CancellationReasonStep/cancellation-reason-step-content.types';
import {
  CancellationReasonStepBase,
  type CancellationReasonStepBaseProps,
  type CancellationReasonSubmitPayload,
} from '../CancellationReasonStep/CancellationReasonStepBase';

type SmartThermostatCancellationReasonMachine = ReturnType<typeof createSmartThermostatCancellationReasonMachine>;

export interface SmartThermostatCancellationReasonFlowContext extends FlowContext {
  reasonCode?: DiscontinueReason;
  intake?: SmartThermostatCancellationReasonMachineContext['intake'];
  explanation?: string;
  reasonLabel?: string;
}

interface Props {
  /** The name the flow's reason step is authored under: `CancelationReasonStep` for the thermostat. */
  contentName: CancellationReasonContentName;
  reasonMachine: SmartThermostatCancellationReasonMachine;
}

export const SmartThermostatCancellationReasonStep: FC<Props> = ({ contentName, reasonMachine }) => {
  const { useFlowActorRef } = useFlowHooks<SmartThermostatCancellationReasonFlowContext>();
  const { send: sendToFlowMachine } = useFlowActorRef();

  const onSuccessfulSubmit = useCallback(
    ({ reason, reasonLabel, explanation, intake }: CancellationReasonSubmitPayload) => {
      sendToFlowMachine({
        type: 'UPDATE_VALUES',
        values: {
          reasonCode: reason as DiscontinueReason,
          reasonLabel,
          explanation,
          intake: intake as SmartThermostatCancellationReasonMachineContext['intake'],
        },
      });
      sendToFlowMachine({ type: 'NEXT_PAGE' });
    },
    [sendToFlowMachine],
  );

  return (
    <CancellationReasonStepBase
      contentName={contentName}
      reasonMachine={reasonMachine as CancellationReasonStepBaseProps['reasonMachine']}
      unauthenticatedAction="login-route"
      disallowMode="notification"
      onSuccessfulSubmit={onSuccessfulSubmit}
    />
  );
};
